import { createContext, PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { LegalMentionsSettings } from '@features/invoicingSettings/types/legalSettings/legalMentionsSettings'

import { useForm, UseFormReturn } from '../../../../../../../../../../../hooks'
import { LegalMentionsType } from '../../../../../enums/legalMentionsType'
import { getLegalMentionsSchema } from '../../../../../utils/getLegalMentionsDetailsSchema'
import { LegalMentionsFormSchema } from '../types/legalMentionsFormSchema'

interface ContextState {
  legalMentionsModalForm: UseFormReturn<LegalMentionsFormSchema>
  submitForm: () => void
  variant: LegalMentionsType
}

const LegalMentionsModalFormContext = createContext<ContextState | undefined>(undefined)

interface LegalMentionsModalFormProviderProps extends PropsWithChildren {
  defaultValues: LegalMentionsSettings
  onSubmit: (formData: LegalMentionsSettings) => void
  variant: LegalMentionsType
}

export const LegalMentionsModalFormContextProvider = ({
  children,
  defaultValues,
  onSubmit,
  variant,
}: LegalMentionsModalFormProviderProps): ReactElement => {
  const { t } = useTranslation()

  const legalMentionsModalForm = useForm<LegalMentionsFormSchema>({
    defaultValues,
    validationSchema: getLegalMentionsSchema(t),
  })

  const { Form, handleSubmit: handleLegalMentionsModalFormSubmit, reset: resetForm } = legalMentionsModalForm

  useEffect(() => {
    if (defaultValues) {
      resetForm(defaultValues)
    }
  }, [defaultValues, resetForm])

  const submitForm = useCallback(() => {
    handleLegalMentionsModalFormSubmit(onSubmit)()
  }, [handleLegalMentionsModalFormSubmit, onSubmit])

  const contextValue = useMemo(
    () => ({
      legalMentionsModalForm,
      submitForm,
      variant,
    }),
    [legalMentionsModalForm, submitForm, variant],
  )

  return (
    <LegalMentionsModalFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </LegalMentionsModalFormContext.Provider>
  )
}

export const useLegalMentionsModalForm = () => {
  const context = useContext(LegalMentionsModalFormContext)

  if (!context) {
    throw new Error('LegalMentionsModalFormContextProvider is missing in the module!')
  }

  return context
}
